import { getRouteApi, useRouter, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { onPeopleChanged, releaseLease, sharedRealms, takeLease } from '../account/sharing.ts';
import { useAccount } from '../account/store.ts';
import { realms } from '../db/client.ts';
import { realmKey } from '../db/protocol.ts';

const route = getRouteApi('/_app/realm/$realmKey');

/** Renewed this often while it's this session's; the server keeps it for three minutes. */
const RENEW_MS = 60_000;
/** While someone else holds it, asked this often whether it's free. */
const WAIT_MS = 30_000;
/** Given up after this long without a key pressed or a click, so someone who walks away doesn't block everyone. */
export const IDLE_MS = 10 * 60_000;

/** The open realm's edit lease, as the app bar shows it (pages/RealmRole.tsx). */
export type LeaseView =
  | { readonly kind: 'mine' }
  | { readonly kind: 'held'; readonly email: string; readonly device: string }
  | { readonly kind: 'away' };

let shown: { file: string; lease: LeaseView } | null = null;
const listeners = new Set<() => void>();
function show(next: { file: string; lease: LeaseView } | null): void {
  shown = next;
  listeners.forEach((listener) => listener());
}

/** The lease of the realm open as `file`, or null when it has none (no co-owners, or someone else's copy to read). */
export function useLeaseView(file: string | null): LeaseView | null {
  const now = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => shown,
  );
  return now && now.file === file ? now.lease : null;
}

/**
 * Takes turns at writing a realm shared with co-owners (docs/SHARING-CO-OWNERS-PLAN.md
 * section 3.4). On opening it, takes the realm's lease, and renews it while the realm is
 * open. While someone else holds it, the realm is read-only here, with a note saying who,
 * and it's asked again every half minute; once free, the newest version is fetched and
 * editing comes back. Ten minutes without a key or a click gives it up.
 */
export function LeaseKeeper() {
  const { realm, shared } = route.useLoaderData();
  const account = useAccount();
  const router = useRouter();
  const file = realm.file;
  const coOwned = shared?.role === 'editor' ? shared : null;
  // Plain values only below: the realm's data is new after every reload, and the lease mustn't be given up for that.
  const isCoOwned = coOwned !== null;
  const me = account.status === 'signed-in' ? account.me.user.id : null;
  const ownerId = coOwned ? coOwned.ownerId : shared === null ? me : null;
  const realmId = coOwned ? coOwned.realmId : realmKey(file);
  const [lease, setLease] = useState<LeaseView | null>(null);
  const [resume, setResume] = useState(0);
  const lastActive = useRef(Date.now());
  /** Asks about the lease again now: on another page of the realm, or when someone is invited or removed. */
  const kick = useRef<() => void>(() => undefined);
  const path = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => kick.current(), [path]);
  useEffect(() => onPeopleChanged(() => kick.current()), []);

  useEffect(() => {
    const active = () => {
      lastActive.current = Date.now();
    };
    window.addEventListener('keydown', active);
    window.addEventListener('pointerdown', active);
    return () => {
      window.removeEventListener('keydown', active);
      window.removeEventListener('pointerdown', active);
    };
  }, []);

  useEffect(() => {
    if (!ownerId) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let held = false;
    let blocked = false;
    const settle = (next: LeaseView | null) => {
      if (!live) return;
      setLease(next);
      show(next ? { file, lease: next } : null);
    };
    const block = async (on: boolean) => {
      if (blocked === on) return;
      blocked = on;
      if (!on) await realms.catchUp(file).catch(() => false);
      await realms.setLease(file, !on);
      if (live) await router.invalidate();
    };
    const later = (ms: number) => {
      if (live) timer = setTimeout(() => void tick(), ms);
    };
    async function tick(): Promise<void> {
      clearTimeout(timer);
      // Only a realm shared with someone can have co-owners; a co-owned one always might.
      if (!isCoOwned) {
        const people = await sharedRealms().catch(() => null);
        if (!live) return;
        if (people && (people.get(realmId) ?? 0) === 0) {
          held = false;
          await block(false);
          settle(null);
          return;
        }
      }
      if (held && Date.now() - lastActive.current > IDLE_MS) {
        held = false;
        await releaseLease(ownerId!, realmId).catch(() => undefined);
        await block(true);
        settle({ kind: 'away' });
        return;
      }
      let answer;
      try {
        answer = await takeLease(ownerId!, realmId);
      } catch {
        // Offline, or the server's busy: as it was, and asked again later.
        later(held ? RENEW_MS : WAIT_MS);
        return;
      }
      if (!live) return;
      if (!answer.needed) {
        // Shared, but with no co-owner yet: asked again, for when one accepts.
        held = false;
        await block(false);
        settle(null);
        later(RENEW_MS);
        return;
      }
      if (answer.mine) {
        // Just taken, not after a wait (lifting a block catches up already): what a co-owner
        // sent while this device wasn't looking comes first, so no edit starts from an old version.
        const fresh = !held && !blocked;
        held = true;
        await block(false);
        if (fresh && (await realms.catchUp(file).catch(() => false)) && live) await router.invalidate();
        settle({ kind: 'mine' });
        later(RENEW_MS);
      } else {
        held = false;
        await block(true);
        settle({ kind: 'held', email: answer.holder.email, device: answer.holder.device });
        later(WAIT_MS);
      }
    }
    lastActive.current = Date.now();
    kick.current = () => {
      // Not while away: that waits for Carry on editing.
      if (live && !(blocked && !held && shown?.lease.kind === 'away')) void tick();
    };
    void tick();
    const leaving = () => {
      if (held) void releaseLease(ownerId, realmId, true);
    };
    window.addEventListener('pagehide', leaving);
    return () => {
      live = false;
      clearTimeout(timer);
      kick.current = () => undefined;
      window.removeEventListener('pagehide', leaving);
      // What's waiting is sent first, so whoever edits next starts from it.
      if (held) void realms.sendNow(file).finally(() => releaseLease(ownerId, realmId).catch(() => undefined));
      // Read-only no longer once it's closed here: opened again, the lease is asked for again.
      if (blocked) void realms.setLease(file, true).catch(() => undefined);
      show(null);
    };
  }, [file, ownerId, realmId, isCoOwned, router, resume]);

  if (lease?.kind === 'held') {
    return (
      <p className="shared-banner lease-banner" role="status">
        <strong>{lease.email}</strong> is editing this realm ({lease.device}). You can read it; it opens for editing when they’re done.
      </p>
    );
  }
  if (lease?.kind === 'away') {
    return (
      <div className="shared-banner lease-banner" role="status">
        <p>You stepped away, so others could edit this realm. It’s read-only here until you carry on.</p>
        <button type="button" className="primary" onClick={() => setResume((n) => n + 1)}>
          Carry on editing
        </button>
      </div>
    );
  }
  return null;
}
